import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, InputAdornment,
  Chip, Alert, CircularProgress, IconButton, Tooltip, TablePagination,
  MenuItem,
} from '@mui/material';
import { Add, Search, Edit, PersonOff, PersonAdd } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { employeeService } from '@smartgrow/shared';

export const EmployeesPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchQuery); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['employees', page, statusFilter, debouncedSearch],
    queryFn: () => employeeService.search({
      page, size: 10,
      search: debouncedSearch || undefined,
      active: statusFilter === 'ALL' ? undefined : statusFilter === 'ACTIVE',
    }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => employeeService.deleteEmployee(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => employeeService.activateEmployee(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });

  const filtered = data?.content ?? [];

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" fontWeight="bold">Employees</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/dashboard/employees/new')}>
          Add Employee
        </Button>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by name, employee number, or position..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ flex: 1, minWidth: 220 }}
          />
          <TextField select size="small" label="Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {isError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load employees.</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Employee #</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>Monthly Salary</TableCell>
              <TableCell>Pay Day</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Hire Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
              </TableRow>
            ) : filtered?.length ? (
              filtered.map((e, i) => (
                <TableRow key={e.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/employees/${e.id}`)}>
                  <TableCell>{page * 10 + i + 1}</TableCell>
                  <TableCell><Typography variant="body2" fontFamily="monospace">{e.employeeNumber}</Typography></TableCell>
                  <TableCell><Typography variant="body2" fontWeight="medium">{e.fullName}</Typography></TableCell>
                  <TableCell>{e.position ?? '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      ₦{e.monthlySalary.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>{e.paymentDay}</TableCell>
                  <TableCell>{e.paymentMethod.replace('_', ' ')}</TableCell>
                  <TableCell>{new Date(e.hireDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip label={e.isActive ? 'Active' : 'Inactive'} color={e.isActive ? 'success' : 'error'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={(ev) => { ev.stopPropagation(); navigate(`/dashboard/employees/${e.id}/edit`); }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {e.isActive ? (
                        <Tooltip title="Deactivate">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              if (globalThis.confirm(`Deactivate ${e.fullName}?`)) {
                                deactivateMutation.mutate(e.id);
                              }
                            }}
                          >
                            <PersonOff fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Activate">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              activateMutation.mutate(e.id);
                            }}
                          >
                            <PersonAdd fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>No employees found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data?.totalElements ?? 0}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={10}
          rowsPerPageOptions={[10]}
        />
      </TableContainer>
    </Box>
  );
};
